/**
 * Goal hooks for React Query
 *
 * Provides data fetching and mutation hooks for goal management.
 * Goals represent desired outcomes that commitments and tasks contribute to.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GoalFilters, CreateGoalInput, UpdateGoalInput, GoalRelationshipType, AddRelationshipInput } from '@/types';
import * as goalsApi from '@/api/goals';

// Query options type
export interface GoalQueryOptions {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Query keys - include options in key for proper cache invalidation
export const goalKeys = {
  all: ['goals'] as const,
  lists: () => [...goalKeys.all, 'list'] as const,
  list: (filters?: GoalFilters, options?: GoalQueryOptions) =>
    [...goalKeys.lists(), { filters, options }] as const,
  detail: (id: string) => [...goalKeys.all, 'detail', id] as const,
  tree: (rootId?: string) => [...goalKeys.all, 'tree', rootId] as const,
  stats: () => [...goalKeys.all, 'stats'] as const,
  byValue: (valueId: string) => [...goalKeys.all, 'byValue', valueId] as const,
  related: (goalId: string, type?: GoalRelationshipType) => 
    [...goalKeys.all, 'related', goalId, type] as const,
  tasks: (goalId: string) => [...goalKeys.all, 'tasks', goalId] as const,
};

/**
 * Hook for listing goals with filters
 */
export function useGoals(
  filters?: GoalFilters,
  options?: GoalQueryOptions
) {
  return useQuery({
    queryKey: goalKeys.list(filters, options),
    queryFn: () => goalsApi.listGoals(filters, options),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
}

/**
 * Hook for getting a single goal
 */
export function useGoal(goalId: string) {
  return useQuery({
    queryKey: goalKeys.detail(goalId),
    queryFn: () => goalsApi.getGoal(goalId),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for getting goals as a hierarchical tree
 */
export function useGoalTree(rootGoalId?: string) {
  return useQuery({
    queryKey: goalKeys.tree(rootGoalId),
    queryFn: () => goalsApi.getGoalTree(rootGoalId),
  });
}

/**
 * Hook for getting goal statistics
 */
export function useGoalStats() {
  return useQuery({
    queryKey: goalKeys.stats(),
    queryFn: () => goalsApi.getGoalStats(),
  });
}

/**
 * Hook for getting goals aligned with a specific value
 */
export function useGoalsByValue(valueId: string) {
  return useQuery({
    queryKey: goalKeys.byValue(valueId),
    queryFn: () => goalsApi.getGoalsByValue(valueId),
    enabled: Boolean(valueId),
  });
}

/**
 * Hook for getting related goals by relationship type
 */
export function useRelatedGoals(goalId: string, type?: GoalRelationshipType) {
  return useQuery({
    queryKey: goalKeys.related(goalId, type),
    queryFn: () => goalsApi.getRelatedGoals(goalId, type),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for getting child goals (goals that have this goal as parent)
 */
export function useChildGoals(goalId: string) {
  return useQuery({
    queryKey: goalKeys.related(goalId, 'child'),
    queryFn: () => goalsApi.getChildGoals(goalId),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for getting parent goals (goals that this goal is a sub-goal of)
 */
export function useParentGoals(goalId: string) {
  return useQuery({
    queryKey: goalKeys.related(goalId, 'parent'),
    queryFn: () => goalsApi.getParentGoals(goalId),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for getting blocking/dependency relationships
 */
export function useDependencies(goalId: string) {
  return useQuery({
    queryKey: goalKeys.related(goalId, 'depends_on'),
    queryFn: () => goalsApi.getDependencies(goalId),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for getting active goals only
 */
export function useActiveGoals() {
  return useGoals({ status: 'active' });
}

/**
 * Hook for getting tasks that support/advance a goal
 */
export function useGoalTasks(goalId: string) {
  return useQuery({
    queryKey: goalKeys.tasks(goalId),
    queryFn: () => goalsApi.getGoalTasks(goalId),
    enabled: Boolean(goalId),
  });
}

/**
 * Hook for goal mutations (create, update, delete, complete, reopen)
 */
export function useGoalMutations() {
  const queryClient = useQueryClient();

  // Invalidate all goal queries
  const invalidateGoals = () => {
    queryClient.invalidateQueries({ queryKey: goalKeys.all });
  };

  // Create goal
  const createGoal = useMutation({
    mutationFn: (input: CreateGoalInput) => goalsApi.createGoal(input),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Update goal
  const updateGoal = useMutation({
    mutationFn: ({ goalId, input }: { goalId: string; input: UpdateGoalInput }) =>
      goalsApi.updateGoal(goalId, input),
    onMutate: async ({ goalId, input }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: goalKeys.detail(goalId) });

      // Snapshot previous value
      const previousGoal = queryClient.getQueryData(goalKeys.detail(goalId));

      // Optimistically update
      queryClient.setQueryData(goalKeys.detail(goalId), (old: { goal: Record<string, unknown> } | undefined) => {
        if (!old || !old.goal) return old;
        return {
          goal: { ...old.goal, ...input },
        };
      });

      return { previousGoal };
    },
    onError: (_err, { goalId }, context) => {
      // Rollback on error
      if (context?.previousGoal) {
        queryClient.setQueryData(goalKeys.detail(goalId), context.previousGoal);
      }
    },
    onSettled: () => {
      invalidateGoals();
    },
  });

  // Delete goal
  const deleteGoal = useMutation({
    mutationFn: (goalId: string) => goalsApi.deleteGoal(goalId),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Complete goal
  const completeGoal = useMutation({
    mutationFn: (goalId: string) => goalsApi.completeGoal(goalId),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Reopen goal
  const reopenGoal = useMutation({
    mutationFn: (goalId: string) => goalsApi.reopenGoal(goalId),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Update progress
  const updateProgress = useMutation({
    mutationFn: ({ goalId, progress }: { goalId: string; progress: number }) =>
      goalsApi.updateGoalProgress(goalId, progress),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Add relationship
  const addRelationship = useMutation({
    mutationFn: ({ goalId, input }: { goalId: string; input: AddRelationshipInput }) =>
      goalsApi.addGoalRelationship(goalId, input),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Remove relationship
  const removeRelationship = useMutation({
    mutationFn: ({ 
      goalId, 
      relatedGoalId, 
      type, 
      bidirectional 
    }: { 
      goalId: string; 
      relatedGoalId: string; 
      type?: GoalRelationshipType; 
      bidirectional?: boolean;
    }) => goalsApi.removeGoalRelationship(goalId, relatedGoalId, { type, bidirectional }),
    onSuccess: () => {
      invalidateGoals();
    },
  });

  // Remove a task's association with a goal (task no longer supports this goal)
  const removeGoalTask = useMutation({
    mutationFn: ({ goalId, taskId }: { goalId: string; taskId: string }) =>
      goalsApi.removeGoalTask(goalId, taskId),
    onSuccess: (_, { goalId }) => {
      // Invalidate goal tasks and task queries
      queryClient.invalidateQueries({ queryKey: goalKeys.tasks(goalId) });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      invalidateGoals();
    },
  });

  return {
    createGoal,
    updateGoal,
    deleteGoal,
    completeGoal,
    reopenGoal,
    updateProgress,
    addRelationship,
    removeRelationship,
    removeGoalTask,
  };
}

